<!--
// app/pages/[region]/Matches.vue
-->
<script setup lang="ts">
import Banner from "~/app/components/Shared/HeaderBannerTitle.vue";
import MatchTurns from "~/app/components/pages/region-matches/MatchTurns.vue";
import { useSeasons } from "~/app/composables/season/seasons";
import type { Team } from "~/src/team/domain/models/Team";
import type { Match } from "~/src/match/domain/models/Match";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import { useWebContext } from "~/app/composables/webContext";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useTeams } from "~/app/composables/teams";
import CardNoDataMessage from "~/app/components/Shared/CardNoDataMessage.vue";
import ButtonEcalSync from "~/app/components/Shared/ButtonEcalSync.vue";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/matches",
    es: "/[region]/partidos",
    de: "/[region]/spiele",
    fr: "/[region]/matchs",
    it: "/[region]/partite",
    pt: "/[region]/jogos",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations", "not-found-mena"],
});

const { t, locale } = useI18n();
const { getSeasonTeams, useFetchParamsSeasonMatchCenterData } = useSeasons();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { contextSiteTitle } = useWebContext();
const { currentSeasonTeams, region } = useCompetitionStore();
const { teamArrToObjById } = useTeams();

const turns = ref<Record<string, Match[]>>();
const teams = ref<Record<number, Team>>();
const matchComponentKey = ref<number>(0);
const loadingSeasonData = ref<boolean>(false);

const getSeasonData = async (): Promise<void> => {
  if (!seasonId.value) {
    console.error("seasonId not defined");
    return;
  }

  try {
    loadingSeasonData.value = true;

    const seasonIdValue = seasonId.value;
    const path = useFetchParamsSeasonMatchCenterData(seasonIdValue, locale.value).path;
    const opts = useFetchParamsSeasonMatchCenterData(seasonIdValue, locale.value).opts;
    const { data, error } = await useFetch(path, opts);

    if (error.value) {
      console.error(error.value);
    }

    turns.value = seasonIdValue ? data.value : undefined;

    if ((seasonIdValue && seasonIdValue === 8) || region.id === 3) {
      //Kingdom cup -> season id 8
      const kingdomTeams = await getSeasonTeams(seasonIdValue);
      teams.value = await teamArrToObjById(kingdomTeams);
    } else {
      teams.value = await teamArrToObjById(currentSeasonTeams);
    }
  } finally {
    loadingSeasonData.value = false;
    matchComponentKey.value++;
  }
};

if (seasonId.value) {
  await getSeasonData();
}

useSeoMeta({
  title: t("header.navCompetition.matches"),
  ogTitle: t("header.navCompetition.matches"),
  description: t("meta.description.matches.page", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.matches.page", { league: region.leagueTitle || contextSiteTitle }),
});
</script>

<template>
  <banner :title="$t('matches.banner.title')"></banner>
  <section class="px-4 pb-6">
    <div class="kql-w-container">
      <div class="flex flex-col items-stretch sm:flex-row sm:items-center justify-between">
        <div class="absolute top-[155px] ltr:right-4 rtl:left-4 sm:static">
          <button-ecal-sync as-button></button-ecal-sync>
        </div>
        <seasons-filter
          v-model:seasonClusterId="seasonClusterId"
          v-model:seasonId="seasonId"
          @change="getSeasonData"
          combined-with-more-filters
        />
      </div>
      <match-turns
        v-if="turns && teams"
        :group-turns="turns"
        :teams="teams"
        :key="matchComponentKey"
        :loading="loadingSeasonData"
      />
      <card-no-data-message
        v-if="(turns && !turns.length) || !turns"
        :message="$t('stats.message.notAvailable')"
      ></card-no-data-message>
    </div>
  </section>
</template>

<style scoped></style>
